import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/dashboard.css';
import '../styles/counselors.css';
import '../styles/applications.css';
import '../styles/visa.css';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import VisaFormFields from '../components/VisaFormFields';
import VisaDocumentSlots from '../components/VisaDocumentSlots';
import { emptyVisaForm } from '../constants/visaFormOptions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPassport, faSave, faTimes, faArrowLeft, faUserGraduate, faSearch, faCopy, faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

const studentFullName = (s) =>
  [s.name, s.middle_name, s.surname].filter((p) => p && String(p).trim()).join(' ').trim();

const visaDisplayName = (v) =>
  (v.student_name || v.applicant_name || '').trim();

export default function CreateVisa() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(emptyVisaForm());
  const [countries, setCountries] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [pendingByType, setPendingByType] = useState({});
  // { type: 'student'|'visa', id, label }
  const [docTransferSource, setDocTransferSource] = useState(null);

  const [pickerMode, setPickerMode] = useState(null); // 'students' | 'visas' | null
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerLoading, setPickerLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [existingVisas, setExistingVisas] = useState([]);

  useEffect(() => {
    fetchCountries();
    fetchCounselors();
  }, []);

  const fetchCountries = async () => {
    try {
      const res = await fetch(`${API_URL}/countries`);
      const data = await res.json();
      if (data.success) setCountries(data.countries.filter((c) => c.status === 'Active'));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCounselors = async () => {
    try {
      const res = await fetch(`${API_URL}/counselors`);
      const data = await res.json();
      if (data.success) setCounselors(data.counselors || []);
    } catch (e) {
      console.error(e);
    }
  };

  const applyContactFromSelection = ({ name, email, phone, transferSource = null }) => {
    setFormData((prev) => ({
      ...prev,
      applicantName: name || prev.applicantName,
      applicantEmail: email || prev.applicantEmail,
      applicantPhone: phone || prev.applicantPhone,
    }));
    setDocTransferSource(transferSource);
    setPickerMode(null);
    setPickerSearch('');
  };

  const openStudentPicker = async () => {
    setPickerMode('students');
    setPickerSearch('');
    setPickerLoading(true);
    try {
      const res = await fetch(`${API_URL}/students`);
      const data = await res.json();
      if (data.success) setStudents(data.students || []);
    } catch (e) {
      console.error(e);
    } finally {
      setPickerLoading(false);
    }
  };

  const openVisaPicker = async () => {
    setPickerMode('visas');
    setPickerSearch('');
    setPickerLoading(true);
    try {
      const res = await fetch(`${API_URL}/visas`);
      const data = await res.json();
      if (data.success) setExistingVisas(data.visas || []);
    } catch (e) {
      console.error(e);
    } finally {
      setPickerLoading(false);
    }
  };

  const closePicker = () => {
    setPickerMode(null);
    setPickerSearch('');
  };

  const filteredStudents = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const name = studentFullName(s).toLowerCase();
      const email = (s.email || '').toLowerCase();
      const mobile = (s.mobile || '').toLowerCase();
      const sid = (s.student_id || '').toLowerCase();
      return name.includes(q) || email.includes(q) || mobile.includes(q) || sid.includes(q);
    });
  }, [students, pickerSearch]);

  const filteredVisas = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return existingVisas;
    return existingVisas.filter((v) => {
      const name = visaDisplayName(v).toLowerCase();
      const email = (v.student_email || v.applicant_email || '').toLowerCase();
      const phone = (v.student_mobile || v.applicant_phone || '').toLowerCase();
      const vid = (v.visa_id || '').toLowerCase();
      const country = (v.country_name || '').toLowerCase();
      return name.includes(q) || email.includes(q) || phone.includes(q) || vid.includes(q) || country.includes(q);
    });
  }, [existingVisas, pickerSearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const queueFile = (type, file) => {
    setPendingByType((prev) => ({
      ...prev,
      [type]: [...(prev[type] || []), file],
    }));
  };

  const removePending = (type, index) => {
    setPendingByType((prev) => ({
      ...prev,
      [type]: (prev[type] || []).filter((_, i) => i !== index),
    }));
  };

  const uploadPendingDocuments = async (visaId) => {
    const entries = Object.entries(pendingByType);
    for (const [documentType, files] of entries) {
      for (const file of files) {
        const fd = new FormData();
        fd.append('document', file);
        fd.append('documentType', documentType);
        await fetch(`${API_URL}/visas/${visaId}/documents`, { method: 'POST', body: fd });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.applicantName || !formData.countryId || !formData.visaType || !formData.visaStatus) {
      setError('Please fill in all required fields (Student Name, Country, Visa Type, Visa Status).');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setLoading(true);
      setError('');
      const payload = { ...formData };
      if (docTransferSource?.type === 'student') {
        payload.studentId = docTransferSource.id;
        payload.transferFromStudentId = docTransferSource.id;
      } else if (docTransferSource?.type === 'visa') {
        payload.transferFromVisaId = docTransferSource.id;
      }

      const res = await fetch(`${API_URL}/visas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        await uploadPendingDocuments(data.visaId);
        const transferred = Number(data.transferredDocuments) || 0;
        alert(
          `Visa created successfully! Visa ID: ${data.generatedVisaId}` +
          (transferred > 0
            ? `\n${transferred} document(s) transferred automatically.`
            : docTransferSource
              ? '\nNo matching documents were available to transfer.'
              : '')
        );
        navigate(`/edit-visa/${data.visaId}`);
      } else {
        setError(data.message || 'Failed to create visa');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (e) {
      setError('Network error. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const pickerTitle = pickerMode === 'students'
    ? 'Select Registered Student'
    : pickerMode === 'visas'
      ? 'Select from Existing Visa Entries'
      : '';

  return (
    <>
      <Sidebar />
      <div className="main-content" id="mainContent">
        <Header />
        <div className="counselors-container">
          <div className="counselors-header">
            <div className="header-left">
              <h1>
                <FontAwesomeIcon icon={faPassport} className="page-icon" />
                Create New Visa
              </h1>
            </div>
            <div className="header-right">
              <Link to="/visa" className="btn-secondary">
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to Visas
              </Link>
            </div>
          </div>

          {error && (
            <div className="visa-enquiry-alert visa-enquiry-alert-error">{error}</div>
          )}

          <div style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
            padding: '16px 20px', marginBottom: '20px', display: 'flex',
            flexWrap: 'wrap', gap: '12px', alignItems: 'center',
          }}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginRight: '4px' }}>
              Autofill contact details:
            </span>
            <button type="button" className="btn-secondary" onClick={openStudentPicker}>
              <FontAwesomeIcon icon={faUserGraduate} style={{ marginRight: '8px' }} />
              Select from registered students
            </button>
            <button type="button" className="btn-secondary" onClick={openVisaPicker}>
              <FontAwesomeIcon icon={faCopy} style={{ marginRight: '8px' }} />
              Select from existing visa entries
            </button>
          </div>

          <form onSubmit={handleSubmit} className="counselor-form">
            <VisaFormFields
              data={formData}
              onChange={handleChange}
              countries={countries}
              counselors={counselors}
            />
            <VisaDocumentSlots
              pendingByType={pendingByType}
              onQueueFile={queueFile}
              onRemovePending={removePending}
              showPending
            />

            {docTransferSource ? (
              <div style={{
                marginTop: '4px',
                padding: '14px 16px',
                background: '#fff7ed',
                border: '1px solid #fdba74',
                borderRadius: '10px',
                color: '#9a3412',
                fontSize: '13px',
                lineHeight: 1.5,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FontAwesomeIcon icon={faInfoCircle} style={{ marginTop: '2px', color: '#F47B20', flexShrink: 0 }} />
                  <div>
                    <strong>Automatic document transfer</strong>
                    {docTransferSource.type === 'student' ? (
                      <>
                        <p style={{ margin: '6px 0 0' }}>
                          When you save this visa, the following documents will be transferred
                          automatically from <strong>{docTransferSource.label}</strong>&apos;s student
                          registration form (if uploaded):
                        </p>
                        <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
                          <li>Passport → Passport</li>
                          <li>Birth Certificate → Birth Certificate</li>
                        </ul>
                      </>
                    ) : (
                      <p style={{ margin: '6px 0 0' }}>
                        When you save this visa, <strong>all documents</strong> already uploaded on
                        visa entry <strong>{docTransferSource.label}</strong> will be copied onto this new visa.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setDocTransferSource(null)}
                      style={{
                        marginTop: '10px', background: 'none', border: 'none', padding: 0,
                        color: '#c2410c', fontSize: '12px', textDecoration: 'underline', cursor: 'pointer',
                      }}
                    >
                      Clear selection (do not transfer documents)
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                Select a registered student or an existing visa entry above to automatically transfer uploaded documents.
              </p>
            )}

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => navigate('/visa')} disabled={loading}>
                <FontAwesomeIcon icon={faTimes} /> Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={loading}>
                <FontAwesomeIcon icon={faSave} /> {loading ? 'Saving...' : 'Save Visa'}
              </button>
            </div>
          </form>
        </div>

        {pickerMode && (
          <>
            <div className="modal-backdrop show" onClick={closePicker}></div>
            <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
              <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                  <div className="modal-header" style={{
                    background: 'linear-gradient(135deg, #F47B20 0%, #e8660f 100%)',
                    padding: '18px 24px', borderBottom: 'none',
                  }}>
                    <h5 className="modal-title" style={{ color: 'white', fontSize: '18px', fontWeight: 700, margin: 0 }}>
                      {pickerTitle}
                    </h5>
                    <button
                      type="button"
                      className="btn-close-modal"
                      onClick={closePicker}
                      style={{
                        color: 'white', background: 'rgba(255,255,255,0.2)', border: 'none',
                        borderRadius: '8px', width: '36px', height: '36px', cursor: 'pointer',
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>

                  <div className="modal-body" style={{ padding: '20px 24px' }}>
                    <div style={{ position: 'relative', marginBottom: '16px' }}>
                      <FontAwesomeIcon
                        icon={faSearch}
                        style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}
                      />
                      <input
                        type="text"
                        className="input-field"
                        placeholder="Search by name, email, phone..."
                        value={pickerSearch}
                        onChange={(e) => setPickerSearch(e.target.value)}
                        style={{ paddingLeft: '36px', marginBottom: 0 }}
                        autoFocus
                      />
                    </div>

                    {pickerLoading ? (
                      <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>Loading...</div>
                    ) : (
                      <div style={{ maxHeight: '420px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                        {pickerMode === 'students' && (
                          filteredStudents.length === 0 ? (
                            <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                              No students found
                            </div>
                          ) : filteredStudents.map((s) => (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => applyContactFromSelection({
                                name: studentFullName(s),
                                email: s.email || '',
                                phone: s.mobile || '',
                                transferSource: {
                                  type: 'student',
                                  id: s.id,
                                  label: studentFullName(s) || s.student_id || 'this student',
                                },
                              })}
                              style={{
                                display: 'block', width: '100%', textAlign: 'left', border: 'none',
                                borderBottom: '1px solid #f3f4f6', background: 'white', padding: '12px 16px',
                                cursor: 'pointer',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#fff7ed'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                            >
                              <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                                {studentFullName(s) || 'Unnamed'}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                {s.student_id && <span>{s.student_id} · </span>}
                                {s.email || 'No email'} · {s.mobile || 'No phone'}
                              </div>
                            </button>
                          ))
                        )}

                        {pickerMode === 'visas' && (
                          filteredVisas.length === 0 ? (
                            <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                              No visa entries found
                            </div>
                          ) : filteredVisas.map((v) => (
                            <button
                              key={v.id}
                              type="button"
                              onClick={() => applyContactFromSelection({
                                name: visaDisplayName(v),
                                email: v.student_email || v.applicant_email || '',
                                phone: v.student_mobile || v.applicant_phone || '',
                                transferSource: {
                                  type: 'visa',
                                  id: v.id,
                                  label: v.visa_id || visaDisplayName(v) || 'selected visa',
                                },
                              })}
                              style={{
                                display: 'block', width: '100%', textAlign: 'left', border: 'none',
                                borderBottom: '1px solid #f3f4f6', background: 'white', padding: '12px 16px',
                                cursor: 'pointer',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#fff7ed'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                            >
                              <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                                {visaDisplayName(v) || 'Unnamed'}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                {v.visa_id && <span>{v.visa_id} · </span>}
                                {(v.student_email || v.applicant_email) || 'No email'} · {(v.student_mobile || v.applicant_phone) || 'No phone'}
                                {v.country_name && <span> · {v.country_name}</span>}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '12px 0 0' }}>
                      Selecting an entry fills Student Name, Email, and Phone Number, and enables automatic document transfer on save.
                    </p>
                  </div>

                  <div className="modal-footer" style={{ borderTop: '1px solid #f3f4f6', padding: '12px 24px' }}>
                    <button type="button" className="btn-secondary" onClick={closePicker}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
